import axios from 'axios'
import type { AxiosError, AxiosRequestConfig } from 'axios'

// API URL - apunta al backend Django
const API_URL = process.env.NEXT_PUBLIC_API_URL || '/api'

// Storage keys
const ACCESS_TOKEN_KEY = 'gia_access_token'
const REFRESH_TOKEN_KEY = 'gia_refresh_token'
const IMPERSONATE_CENTRO_KEY = 'gia_impersonate_centro'

// Create axios instance
export const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Token management functions
export const getAccessToken = (): string | null => {
  if (typeof window === 'undefined') return null

  return localStorage.getItem(ACCESS_TOKEN_KEY)
}

export const getRefreshToken = (): string | null => {
  if (typeof window === 'undefined') return null

  return localStorage.getItem(REFRESH_TOKEN_KEY)
}

export const setTokens = (access: string, refresh: string): void => {
  localStorage.setItem(ACCESS_TOKEN_KEY, access)
  localStorage.setItem(REFRESH_TOKEN_KEY, refresh)
}

export const clearTokens = (): void => {
  localStorage.removeItem(ACCESS_TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
}

export const getImpersonatedCentroId = (): number | null => {
  if (typeof window === 'undefined') return null
  const value = localStorage.getItem(IMPERSONATE_CENTRO_KEY)

  if (!value) return null

  const parsed = Number(value)

  return Number.isFinite(parsed) ? parsed : null
}

export const setImpersonatedCentroId = (centroId: number | null): void => {
  if (typeof window === 'undefined') return

  if (!centroId) {
    localStorage.removeItem(IMPERSONATE_CENTRO_KEY)

    return
  }

  localStorage.setItem(IMPERSONATE_CENTRO_KEY, String(centroId))
}

type RetriableRequestConfig = {
  _retry?: boolean
  url?: string
  headers?: Record<string, string>
}

let refreshPromise: Promise<string> | null = null
const inFlightGetRequests = new Map<string, Promise<any>>()

const serializeParams = (params: Record<string, any> = {}) => {
  const pairs: string[] = []
  const keys = Object.keys(params).sort()

  keys.forEach(key => {
    const value = params[key]

    if (value === undefined || value === null || value === '') return

    if (Array.isArray(value)) {
      value.forEach(item => {
        pairs.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(item))}`)
      })

      return
    }

    pairs.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
  })

  return pairs.join('&')
}

const buildGetRequestKey = (url: string, config?: AxiosRequestConfig) => {
  const paramsRaw = (config?.params || {}) as Record<string, any>
  const params = serializeParams(paramsRaw)
  const centro = getImpersonatedCentroId()
  const centroPart = centro ? `centro=${centro}` : 'centro='

  return `${url}?${params}|${centroPart}`
}

export const getWithDedupe = async <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> => {
  const key = buildGetRequestKey(url, config)
  const existing = inFlightGetRequests.get(key)

  if (existing) {
    return existing as Promise<T>
  }

  const requestPromise = apiClient
    .get<T>(url, config)
    .then(response => response.data)
    .finally(() => {
      inFlightGetRequests.delete(key)
    })

  inFlightGetRequests.set(key, requestPromise)

  return requestPromise
}

const redirectToLogin = () => {
  if (typeof window === 'undefined') return
  if (window.location.pathname === '/es/login') return
  window.location.href = '/es/login'
}

export const refreshAccessToken = async (): Promise<string> => {
  if (refreshPromise) return refreshPromise

  const refreshToken = getRefreshToken()

  if (!refreshToken) {
    throw new Error('No refresh token available')
  }

  refreshPromise = axios
    .post<{ access: string }>(`${API_URL}/token/refresh/`, {
      refresh: refreshToken
    })
    .then(response => {
      const { access } = response.data

      setTokens(access, refreshToken)

      return access
    })
    .finally(() => {
      refreshPromise = null
    })

  return refreshPromise
}

// Request interceptor - Add auth token
apiClient.interceptors.request.use(
  config => {
    const token = getAccessToken()
    const impersonatedCentroId = getImpersonatedCentroId()

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }

    if (config.headers) {
      if (impersonatedCentroId) {
        config.headers['X-Centro-Id'] = String(impersonatedCentroId)
      } else if ('X-Centro-Id' in config.headers) {
        delete config.headers['X-Centro-Id']
      }
    }

    return config
  },
  error => Promise.reject(error)
)

// Response interceptor - Handle token refresh
apiClient.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const originalRequest = (error.config || {}) as RetriableRequestConfig

    // If error is 401 and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't try to refresh if this is the refresh endpoint itself
      if (originalRequest.url?.includes('/token/refresh')) {
        clearTokens()
        redirectToLogin()

        return Promise.reject(error)
      }

      originalRequest._retry = true

      try {
        const access = await refreshAccessToken()

        if (!originalRequest.headers) originalRequest.headers = {}
        originalRequest.headers.Authorization = `Bearer ${access}`

        return apiClient(originalRequest as any)
      } catch (refreshError) {
        clearTokens()
        redirectToLogin()

        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  }
)

export default apiClient
